import {
  CustomerOrderStatus,
  OrderItemStatus,
  OrderStatus,
  type Order,
  type OrderItem,
} from '@/lib/shared-types';
import { toCustomerStatus } from '@/lib/domain';

/**
 * How much of the kitchen the venue is willing to show a guest.
 *
 * Queue position is deliberately absent: it is never exposed to a guest, so
 * there is no flag to get wrong and no screen in this app renders it.
 */
/**
 * The QR session response always carries the branch's currency, so this is a
 * last-resort guard against a malformed payload — not a business default. It is
 * the ONE place a code is written literally in this app; every screen reads
 * `session.currency`, so a venue on KWD cannot end up with a riyal-formatted
 * total because one screen forgot to thread it through.
 */
export const GUEST_FALLBACK_CURRENCY = 'SAR';

export interface GuestTransparency {
  showEta: boolean;
  showItemStatus: boolean;
  showStationName: boolean;
  showDelayUpdates: boolean;
}

/**
 * What a venue gets if it has not decided. Item status is on because it is the
 * whole point of the tracking screen; the station name is off because "Grill 2"
 * is an internal label that means nothing to a guest.
 */
export const DEFAULT_TRANSPARENCY: GuestTransparency = {
  showEta: true,
  showItemStatus: true,
  showStationName: false,
  showDelayUpdates: true,
};

/**
 * How an order leaves this app, which is a venue policy and not a guest choice.
 *
 * With `requireWaiterConfirmation` on, tapping "place the order" no longer
 * reaches a kitchen: the order parks at CONFIRMED and a waiter walks over,
 * reads it back at the table and sends it.
 *
 * Read for the CART, and only for the cart. Before an order exists there is no
 * order-level `awaitingWaiterConfirmation` to consult, so this is the one signal
 * available for warning the guest what is about to happen to their basket. The
 * tracker deliberately ignores it and trusts the server's per-order flag — see
 * `isAwaitingWaiterConfirmation` for why a second copy of the rule is a
 * liability there.
 */
export interface GuestOrdering {
  requireWaiterConfirmation: boolean;
  /**
   * The venue's own deadline for that walk over, in seconds.
   *
   * Read so the shape is whole and a staff surface reading this session gets it,
   * and deliberately never rendered to a guest. A countdown is a promise the
   * venue may miss on a busy floor, and a promise broken in front of the guest
   * is worse than the silence it replaced.
   */
  confirmationSlaSeconds: number;
}

/**
 * What a venue gets if it has not decided: the behaviour this app shipped with,
 * where the order goes straight through.
 *
 * This default is safe in the direction it is now used and would not have been
 * in the direction it once was. In the cart it governs a FOREWARNING with no
 * server-side flag behind it, so guessing "yes" would promise a waiter to every
 * guest at every venue that has not upgraded. On the tracker the same guess
 * would have gone the other way — suppressing a notice for an order the server
 * had already declared to be waiting — which is why the tracker no longer asks.
 */
export const DEFAULT_ORDERING: GuestOrdering = {
  requireWaiterConfirmation: false,
  confirmationSlaSeconds: 0,
};

export interface GuestSession {
  sessionId: string;
  branchId: string;
  branchName: string;
  tableId: string | null;
  tableNumber: string | null;
  currency: string;
  /**
   * The PIN the REST of this table must type to join the same visit, handed
   * back only to the scan that opened it — null for everyone who joined
   * afterwards, and null at a venue that does not gate joining at all.
   *
   * This is the whole of what used to be "invite someone to my party": a second
   * phone scans the same table tent, the API sees a visit already open there and
   * asks for this number. Nothing else about the group is modelled any more.
   */
  sessionPin: string | null;
  /**
   * Beyond the nine keys `POST /sessions/qr` guarantees. The API does not send
   * these yet; every one of them degrades to a safe default so the screens work
   * either way and light up the moment the endpoint grows them.
   */
  tenantId: string | null;
  /** Guest session token (`typ: 'guest'`) — the credential for HTTP and the socket. */
  token: string | null;
  transparency: GuestTransparency;
  ordering: GuestOrdering;
  /**
   * The QR token this session was opened with.
   *
   * Persisted because it is the only way to tell "the guest reloaded the page
   * at their own table" from "the guest scanned a **different** table". Without
   * it a restored session looks identical to a fresh scan, the new token is
   * discarded, and every order goes to the previous table's session.
   */
  qrToken: string | null;
}

// ------------------------------------------------------------------ parsing

type Dict = Record<string, unknown>;

const asDict = (value: unknown): Dict => (value && typeof value === 'object' ? (value as Dict) : {});

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/**
 * Every list endpoint answers `{ success, data: items, meta }`, which the shared
 * axios client unwraps to `{ data, meta }` — but the handful of guest routes
 * that are not paginated answer a bare array. Read both rather than making each
 * screen guess which kind it is talking to.
 */
export function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const body = asDict(payload);
  if (Array.isArray(body.data)) return body.data as T[];
  if (Array.isArray(body.items)) return body.items as T[];
  return [];
}

/** Base64url → UTF-8, without `atob`, which React Native does not guarantee. */
function decodeBase64Url(input: string): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const char of input) {
    const index = alphabet.indexOf(char);
    if (index < 0) continue; // '=' padding and stray whitespace
    buffer = (buffer << 6) | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }

  let out = '';
  for (let i = 0; i < bytes.length; ) {
    const b = bytes[i++];
    if (b < 0x80) out += String.fromCharCode(b);
    else if (b < 0xe0) out += String.fromCharCode(((b & 0x1f) << 6) | (bytes[i++] & 0x3f));
    else if (b < 0xf0)
      out += String.fromCharCode(
        ((b & 0x0f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f),
      );
    else {
      const code =
        ((b & 0x07) << 18) |
        ((bytes[i++] & 0x3f) << 12) |
        ((bytes[i++] & 0x3f) << 6) |
        (bytes[i++] & 0x3f);
      const surrogate = code - 0x10000;
      out += String.fromCharCode(0xd800 + (surrogate >> 10), 0xdc00 + (surrogate & 0x3ff));
    }
  }
  return out;
}

/**
 * Read the claims out of a guest token without verifying it — the server is the
 * only party that may trust this. All this client wants is `tid`, so it can
 * address the right tenant; the token names nothing wider than one session.
 */
export function guestTokenClaims(token: string | null): Dict {
  if (!token) return {};
  const payload = token.split('.')[1];
  if (!payload) return {};
  try {
    return asDict(JSON.parse(decodeBase64Url(payload)));
  } catch {
    return {};
  }
}

/** Merge whatever the API offers over the defaults, ignoring non-booleans. */
export function readTransparency(source: unknown): GuestTransparency {
  const raw = asDict(source);
  const pick = (fallback: boolean, ...names: string[]): boolean => {
    for (const name of names) if (typeof raw[name] === 'boolean') return raw[name] as boolean;
    return fallback;
  };
  return {
    showEta: pick(DEFAULT_TRANSPARENCY.showEta, 'showEta', 'eta'),
    showItemStatus: pick(DEFAULT_TRANSPARENCY.showItemStatus, 'showItemStatus', 'itemStatus'),
    showStationName: pick(DEFAULT_TRANSPARENCY.showStationName, 'showStationName', 'stationName'),
    showDelayUpdates: pick(
      DEFAULT_TRANSPARENCY.showDelayUpdates,
      'showDelayUpdates',
      'delayUpdates',
    ),
  };
}

/**
 * The same treatment for the ordering policy, and for the same reason: a build
 * of this app is in a guest's browser cache long after the API grows a field, so
 * the object may be absent, half-filled, or not an object at all. Every miss
 * lands on `DEFAULT_ORDERING`, which is the old behaviour, so a malformed
 * payload can only ever cost the guest the notice — never invent one.
 */
export function readOrdering(source: unknown): GuestOrdering {
  const raw = asDict(source);
  const seconds = raw.confirmationSlaSeconds ?? raw.confirmationSla;
  return {
    requireWaiterConfirmation:
      typeof raw.requireWaiterConfirmation === 'boolean'
        ? raw.requireWaiterConfirmation
        : DEFAULT_ORDERING.requireWaiterConfirmation,
    confirmationSlaSeconds:
      typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0
        ? seconds
        : DEFAULT_ORDERING.confirmationSlaSeconds,
  };
}

/**
 * `POST /sessions/qr` is contractually nine keys. Anything richer — the
 * transparency settings under a different name — is read defensively from
 * wherever the API chooses to put it, so this client neither breaks without it
 * nor has to be rewritten when it lands.
 *
 * The session id also falls back to the token's `sub`: the token names exactly
 * one session and is the credential every later request is confined by, so a
 * body that somehow omitted the id still cannot leave this client addressing a
 * different visit from the one it is authenticated for.
 */
export function normalizeSession(raw: unknown, qrToken: string | null = null): GuestSession {
  const body = asDict(raw);
  const token =
    asString(body.guestToken) ?? asString(body.token) ?? asString(body.accessToken) ?? null;
  const claims = guestTokenClaims(token);
  const settings = asDict(body.settings);

  return {
    sessionId: String(body.sessionId ?? claims.sub ?? ''),
    branchId: String(body.branchId ?? claims.bid ?? ''),
    branchName: String(body.branchName ?? ''),
    tableId: asString(body.tableId) ?? asString(claims.tbl),
    tableNumber: asString(body.tableNumber),
    currency: String(body.currency ?? GUEST_FALLBACK_CURRENCY),
    sessionPin: asString(body.sessionPin),
    tenantId: asString(body.tenantId) ?? asString(claims.tid),
    token,
    transparency: readTransparency(
      body.transparency ?? settings.transparency ?? settings.guestTransparency,
    ),
    ordering: readOrdering(body.ordering ?? settings.ordering ?? settings.guestOrdering),
    qrToken,
  };
}

// ------------------------------------------------------------------- errors

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: { message?: unknown; code?: unknown; pinRequired?: unknown; requiresPin?: unknown };
  };
}

export function errorMessage(error: unknown, fallback: string): string {
  const data = (error as ApiErrorShape)?.response?.data;
  const message = data?.message;
  if (Array.isArray(message) && message.length) return String(message[0]);
  if (typeof message === 'string' && message) return message;
  return fallback;
}

/**
 * A venue may put a PIN on the table. The client cannot know that in
 * advance, so it tries without one and prompts ONLY when the API says a PIN is
 * the thing that is missing: a `pinRequired` flag, an error code naming the
 * PIN, or a 401/403 whose message names it.
 *
 * A bare status code is never enough: an unrecognised token answers 404 ("This
 * QR code is not recognised"), and a proxy, a rate limiter or a stale
 * credential can answer 401 with no PIN in it. A PIN box for any of those tells
 * the guest to type a number that cannot work and hides the real reason.
 */
export function isPinRequired(error: unknown): boolean {
  const response = (error as ApiErrorShape)?.response;
  if (!response) return false;
  const data = response.data ?? {};

  // The explicit contracts first: a flag or a code is the API saying it outright.
  if (data.pinRequired === true || data.requiresPin === true) return true;
  if (/pin/i.test(String(data.code ?? ''))) return true;

  // Otherwise only a refusal that names the PIN counts, never the status alone.
  if (response.status !== 401 && response.status !== 403) return false;
  const message = Array.isArray(data.message)
    ? (data.message as unknown[]).join(' ')
    : String(data.message ?? '');
  return /\bpins?\b/i.test(message);
}

// -------------------------------------------------------------- projections

/**
 * An order as the tracking screens actually receive it.
 *
 * Neither extra field is on `Order` in `shared-types`, which mirrors the Prisma
 * model: `scheduledFor` belongs to a takeaway's release, and
 * `awaitingWaiterConfirmation` is computed per request and never stored. Both
 * are declared here once because `Track` and `GuestLineList` render the same
 * rows from the same fetch, and a field added to one screen's private copy of
 * this type and forgotten in the other is a tracker that disagrees with itself.
 */
export type TrackedOrder = Order & {
  scheduledFor?: string | null;
  awaitingWaiterConfirmation?: boolean | null;
};

/**
 * Whether this order is sitting on the floor waiting for a waiter, rather than
 * on a pass waiting for a cook.
 *
 * **The venue's own switch is deliberately NOT consulted here.** The server
 * computes `awaitingWaiterConfirmation` as "this order is CONFIRMED, a guest
 * placed it, nobody has sent it, *and this branch requires confirmation*" — the
 * setting is already inside the flag. ANDing `ordering.requireWaiterConfirmation`
 * over the top is a second copy of a rule the server has applied, and a copy can
 * only ever disagree in one direction: it suppresses a notice the server said to
 * show. That is the exact failure this feature exists to fix — a guest reading
 * "Order received" while a waiter is genuinely walking towards them — and it
 * happens for real whenever the bootstrap omits `ordering` or carries a stale
 * copy of it. The un-upgraded venue the check appeared to guard needs no guard:
 * an API that has never heard of this feature sends no flag, so the term below
 * is already false.
 *
 * So two conditions, and the second is not a duplicate of anything:
 *
 *  - **The API says so.** `awaitingWaiterConfirmation` is the server's own
 *    computation, never re-derived here, for the same reason `confirmationDueAt`
 *    is computed there: the rule belongs with the settings it reads.
 *  - **The order is still at CONFIRMED.** The flag is computed per response and
 *    a list fetched a moment before the waiter pressed send carries a stale
 *    `true`. Pinning it to the one status it can legitimately describe means a
 *    late repaint reads as "the kitchen has it", which is the truth, instead of
 *    holding the guest on a waiter who has already been and gone.
 */
export function isAwaitingWaiterConfirmation(
  order: Pick<TrackedOrder, 'status' | 'awaitingWaiterConfirmation'>,
): boolean {
  if (order.awaitingWaiterConfirmation !== true) return false;
  return order.status === OrderStatus.CONFIRMED;
}

/**
 * One line's state, in the guest's vocabulary. A single line is an
 * order of one, so it reuses `toCustomerStatus` wherever that projection has an
 * opinion. Only the two states it collapses (a line picked up or served) and
 * the two it cannot see before the kitchen has the order are mapped here.
 *
 * Returns `null` for a cancelled line — it is not a status, it is an absence.
 */
export function itemCustomerStatus(status: OrderItemStatus): CustomerOrderStatus | null {
  switch (status) {
    case OrderItemStatus.CANCELLED:
      return null;
    case OrderItemStatus.PENDING:
      return CustomerOrderStatus.ORDER_RECEIVED;
    case OrderItemStatus.QUEUED:
      return CustomerOrderStatus.CONFIRMED;
    case OrderItemStatus.PICKED_UP:
      return CustomerOrderStatus.PICKED_UP;
    case OrderItemStatus.SERVED:
      return CustomerOrderStatus.SERVED;
    default:
      return toCustomerStatus(OrderStatus.SENT_TO_KITCHEN, [{ status }]);
  }
}

/** Orders a guest may still act on — a cancelled or voided order is not offered. */
export function isLiveOrder(order: Pick<Order, 'status'>): boolean {
  return (
    order.status !== OrderStatus.CANCELLED &&
    order.status !== OrderStatus.VOIDED &&
    order.status !== OrderStatus.REFUNDED
  );
}

/** Lines worth re-adding to the cart — a cancelled line is not part of "the same again". */
export function reorderableItems(order: Order): OrderItem[] {
  return (order.items ?? []).filter((item) => item.status !== OrderItemStatus.CANCELLED);
}

/**
 * When the venue publishes delay updates, this is the only signal the
 * guest client actually has: an order whose promised time has passed while the
 * food is not ready. No invented ETA, and never a queue position.
 */
export function isRunningLate(order: Order & { scheduledFor?: string | null }, now: number): boolean {
  if (!order.scheduledFor) return false;
  const due = Date.parse(order.scheduledFor);
  if (!Number.isFinite(due) || due > now) return false;
  const status = toCustomerStatus(order.status, order.items ?? []);
  return (
    status !== CustomerOrderStatus.READY &&
    status !== CustomerOrderStatus.SERVED &&
    status !== CustomerOrderStatus.PICKED_UP &&
    status !== CustomerOrderStatus.COMPLETED
  );
}
